const normalize = (value) => String(value || '').trim().toUpperCase();
const phaseIdentity = (item) => normalize(item?.phaseCode || (item?.itemType === 'phase' ? item.title || item.designation : ''));
const sortByDisplayOrder = (left, right) => {
  const leftOrder = left.item.displayOrder == null ? left.index : Number(left.item.displayOrder);
  const rightOrder = right.item.displayOrder == null ? right.index : Number(right.item.displayOrder);
  return leftOrder - rightOrder || Number(left.item.id || 0) - Number(right.item.id || 0) || left.index - right.index;
};
const validDateOnly = (value) => {
  const dateOnly = value ? String(value).slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  const date = new Date(`${dateOnly}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === dateOnly ? dateOnly : null;
};
const projectPhaseDates = (phase, children = []) => {
  const explicitStart = validDateOnly(phase.startDate);
  const explicitEnd = validDateOnly(phase.endDate);
  if (explicitStart || explicitEnd) return { ...phase, startDate: explicitStart, endDate: explicitEnd };

  const datedChildren = children.map((child) => {
    const startDate = validDateOnly(child.startDate);
    const endDate = validDateOnly(child.endDate);
    return { startDate: startDate || endDate, endDate: endDate || startDate };
  }).filter((child) => child.startDate || child.endDate);
  if (!datedChildren.length) return phase;
  return {
    ...phase,
    startDate: datedChildren.map((child) => child.startDate).filter(Boolean).sort()[0] || null,
    endDate: datedChildren.map((child) => child.endDate).filter(Boolean).sort().at(-1) || null,
    ganttDatesDerived: true,
  };
};

export const preparePlanningHierarchy = (items = [], phaseOrder = []) => {
  const indexed = items.map((item, index) => ({ item, index }));
  const phaseRows = indexed.filter(({ item }) => item.itemType === 'phase' && phaseIdentity(item));
  const phaseRanks = new Map(phaseOrder.map((phase, index) => [normalize(phase), index]));
  const phaseCodes = [...new Set(indexed.map(({ item }) => phaseIdentity(item)).filter(Boolean))];
  const explicitPhaseRanks = new Map();
  phaseRows.slice().sort(sortByDisplayOrder).forEach(({ item }) => {
    const code = phaseIdentity(item);
    if (!explicitPhaseRanks.has(code)) explicitPhaseRanks.set(code, explicitPhaseRanks.size);
  });
  const orderedPhaseCodes = phaseCodes.sort((left, right) => {
    const explicitCompare = (explicitPhaseRanks.get(left) ?? Number.MAX_SAFE_INTEGER) - (explicitPhaseRanks.get(right) ?? Number.MAX_SAFE_INTEGER);
    if (explicitCompare) return explicitCompare;
    return (phaseRanks.get(left) ?? phaseRanks.size) - (phaseRanks.get(right) ?? phaseRanks.size) || left.localeCompare(right);
  });
  const groups = orderedPhaseCodes.map((code) => {
    const phases = phaseRows.filter(({ item }) => phaseIdentity(item) === code).sort(sortByDisplayOrder).map(({ item }) => item);
    return {
      code,
      phase: phases[0] || null,
      phases,
      items: indexed.filter(({ item }) => item.itemType !== 'phase' && normalize(item.phaseCode) === code).sort(sortByDisplayOrder).map(({ item }) => item),
    };
  });
  const unkeyedPhases = indexed.filter(({ item }) => item.itemType === 'phase' && !phaseIdentity(item)).sort(sortByDisplayOrder);
  unkeyedPhases.forEach(({ item }) => groups.push({ code: `PHASE-${item.clientKey || item.id || 'SANS-CODE'}`, phase: item, phases: [item], items: [] }));
  const unphased = indexed.filter(({ item }) => item.itemType !== 'phase' && !normalize(item.phaseCode)).sort(sortByDisplayOrder).map(({ item }) => item);
  if (unphased.length) groups.push({ code: 'AUTRE', phase: null, phases: [], items: unphased });
  return groups;
};

export const flattenPlanningHierarchy = (items = [], phaseOrder = []) => preparePlanningHierarchy(items, phaseOrder).flatMap((group) => [...(group.phases || []), ...group.items]);
export const orderPlanningItems = (items = [], phaseOrder = []) => flattenPlanningHierarchy(items, phaseOrder);
export const groupPlanningItemsByPhase = (items = [], phaseOrder = []) => preparePlanningHierarchy(items, phaseOrder);
export const buildPlanningGanttRows = (items = [], phaseOrder = []) => {
  const indexed = items.map((item, index) => ({ item, index }));
  const phases = indexed.filter(({ item }) => item.itemType === 'phase').sort(sortByDisplayOrder);
  const childrenByParent = new Map(phases.map(({ item }) => [item.clientKey, []]));
  const unresolvedByCode = new Map();
  const unphased = [];

  indexed.filter(({ item }) => item.itemType !== 'phase').forEach(({ item, index }) => {
    const explicitParent = item.phaseParentKey && childrenByParent.has(item.phaseParentKey) ? item.phaseParentKey : null;
    const code = normalize(item.phaseCode);
    const exactMatches = code ? phases.filter(({ item: phase }) => normalize(phase.title || phase.designation) === code) : [];
    const resolvedParent = explicitParent || (exactMatches.length === 1 ? exactMatches[0].item.clientKey : null);
    if (resolvedParent) childrenByParent.get(resolvedParent).push({ item, index });
    else if (code) {
      if (!unresolvedByCode.has(code)) unresolvedByCode.set(code, []);
      unresolvedByCode.get(code).push({ item, index });
    } else unphased.push({ item, index });
  });

  const rows = [];
  phases.forEach(({ item: phase }) => {
    const children = childrenByParent.get(phase.clientKey).sort(sortByDisplayOrder);
    const projectedPhase = projectPhaseDates(phase, children.map(({ item }) => item));
    rows.push({ ...projectedPhase, ganttKind: 'phase', ganttLabel: phase.title || phase.designation || phase.phaseCode || 'Phase' });
    children.forEach(({ item }) => rows.push({ ...item, ganttKind: 'item', ganttParentKey: phase.clientKey }));
  });

  const phaseRanks = new Map(phaseOrder.map((phase, index) => [normalize(phase), index]));
  [...unresolvedByCode.entries()].sort(([left], [right]) => (phaseRanks.get(left) ?? phaseRanks.size) - (phaseRanks.get(right) ?? phaseRanks.size) || left.localeCompare(right)).forEach(([code, children]) => {
    const syntheticKey = `gantt-phase-${code}`;
    const sortedChildren = children.sort(sortByDisplayOrder);
    const projectedPhase = projectPhaseDates({ clientKey: syntheticKey, itemType: 'phase', source: 'icc', phaseCode: code, title: code, startDate: null, endDate: null, isSyntheticPhase: true }, sortedChildren.map(({ item }) => item));
    rows.push({ ...projectedPhase, ganttKind: 'phase', ganttLabel: code });
    sortedChildren.forEach(({ item }) => rows.push({ ...item, ganttKind: 'item', ganttParentKey: syntheticKey }));
  });
  unphased.sort(sortByDisplayOrder).forEach(({ item }) => rows.push({ ...item, ganttKind: 'item', ganttParentKey: null }));
  return rows;
};
